import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Bloque de compra de KOTASHI.
 *
 * Cuando el cliente elige "Suscríbete y ahorra", el carrito recibe DOS líneas en una sola
 * petición: la fuente (compra única) y los filtros con el selling plan de Seal. Van juntas a
 * propósito: el descuento automático de Shopify solo quita los 10 € de la fuente si los
 * filtros están en el carrito, así que añadirlas por separado dejaría un instante con el
 * precio sin descontar.
 */
class KotashiCompra extends HTMLElement {
  connectedCallback() {
    this.boton = this.querySelector('[ref="boton"]');
    this.error = this.querySelector('[ref="error"]');
    this.anuncio = this.querySelector('[ref="anuncio"]');
    this.textoBoton = this.querySelector('[data-boton-texto]');
    this.textoBotonOriginal = this.textoBoton?.textContent.trim() ?? 'Añadir al carrito';

    this.addEventListener('change', this.#alCambiarPlan);
    this.addEventListener('click', this.#alClicSwatch);
    this.boton?.addEventListener('click', this.#alAñadir);

    this.#preseleccionarDesdeUrl();
  }

  /**
   * Los enlaces de "Elige tu Mizu" y del menú ☰ llegan aquí con ?color=negro o ?color=blanco
   * (más legible que un id de variante suelto). Sin esto, el color por defecto de la sección
   * (Negro) ganaba siempre, así que pulsar "Comprar" en Mizu Blanca te dejaba viendo — y a
   * punto de comprar — la negra.
   *
   * Se sigue aceptando ?variant=<id> por si queda algún enlace viejo suelto (emails, redes,
   * favoritos guardados): mismo comportamiento, solo cambia de dónde se saca el input a marcar.
   *
   * La tarjeta "Filtros" de esa misma sección enlaza en cambio con ?modo=filtros: como los
   * filtros no son una variante de color sino una tarjeta aparte (el botón
   * [data-swatch-filtros]), hace falta comprobarlo por separado y "pulsar" ese botón para que
   * la persona llegue ya viendo el modo de compra de los filtros, no el de la fuente.
   */
  #preseleccionarDesdeUrl() {
    const parametros = new URLSearchParams(window.location.search);

    if (parametros.get('modo') === 'filtros') {
      this.querySelector('[data-swatch-filtros]')?.click();
      this.#limpiarUrl();
      return;
    }

    const color = parametros.get('color');
    if (color) {
      // Sin acentos y en minúsculas a ambos lados: así "Negro", "negro" o "NEGRO" en la URL
      // encuentran el mismo swatch sin depender de mayúsculas exactas.
      const normalizar = (texto) => texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
      const objetivo = normalizar(color);
      const input = [...this.querySelectorAll('input[data-variant-id]')].find(
        (candidato) => normalizar(candidato.value) === objetivo
      );
      if (input && !input.checked) input.closest('.kotashi-color')?.click();
      this.#limpiarUrl();
      return;
    }

    const variantId = parametros.get('variant');
    if (!variantId) return;

    const input = this.querySelector(`input[data-variant-id="${CSS.escape(variantId)}"]`);
    if (!input || input.checked) return;

    input.closest('.kotashi-color')?.click();
    this.#limpiarUrl();
  }

  /**
   * Borra de la barra de direcciones los parámetros que ya han hecho su trabajo
   * (?modo=filtros, ?color=blanco, ?variant=123). Petición de Fran, 29 oct 2026: esas
   * coletillas quedaban colgando y ensuciaban una URL que si no es limpia y bonita.
   *
   * Es solo cosmético y se hace DESPUÉS de preseleccionar, así que no cambia nada de lo
   * que ve el cliente: la variante correcta ya está marcada cuando esto se ejecuta. Los
   * enlaces con parámetros que haya por ahí fuera (menú, emails, anuncios, favoritos)
   * siguen funcionando igual; lo único que pasa es que al llegar se limpian.
   *
   * replaceState y no pushState a propósito: no queremos crear una entrada nueva en el
   * historial, que obligaría a pulsar "atrás" dos veces para salir de la página.
   */
  #limpiarUrl() {
    try {
      const url = new URL(window.location.href);
      let tocado = false;

      ['modo', 'color', 'variant'].forEach((clave) => {
        if (url.searchParams.has(clave)) {
          url.searchParams.delete(clave);
          tocado = true;
        }
      });

      if (!tocado) return;

      const query = url.searchParams.toString();
      window.history.replaceState({}, '', url.pathname + (query ? `?${query}` : '') + url.hash);
    } catch (e) {
      // Si el navegador no deja tocar el historial, la URL se queda como estaba y ya.
      // Es puramente estético: no vale la pena romper nada por esto.
    }
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#alCambiarPlan);
    this.removeEventListener('click', this.#alClicSwatch);
    this.boton?.removeEventListener('click', this.#alAñadir);
  }

  /**
   * "fuente" (por defecto) o "filtros": cuál de las dos tarjetas de producto está activa.
   * Cambia qué variante se añade al carrito y qué grupo de "Modo de compra" (con sus
   * precios) se ve, porque la fuente y los filtros no valen lo mismo ni se compran igual.
   */
  get #modo() {
    return this.dataset.modo === 'filtros' ? 'filtros' : 'fuente';
  }

  get #variantId() {
    if (this.#modo === 'filtros') return this.dataset.filtrosId;
    const marcado = this.querySelector('input[name^="kotashi-color"]:checked');
    // Sin selector de color (producto de una sola variante) el id viaja en el dataset.
    return marcado?.dataset.variantId ?? this.dataset.variantId;
  }

  get #esSuscripcion() {
    // Ojo: NO vale un selector global `[name^="kotashi-plan"]` a secas, porque el de filtros
    // se llama "kotashi-plan-filtros-..." y también empieza por "kotashi-plan": pillaría el
    // radio marcado del grupo equivocado. Se busca dentro del grupo visible, no en toda la web.
    const grupo = this.querySelector(
      this.#modo === 'filtros' ? '[data-plan-group="filtros"]' : '[data-plan-group="fuente"]'
    );
    const marcado = grupo?.querySelector('input[type="radio"]:checked');
    return marcado?.value === 'sub';
  }

  #alCambiarPlan = (evento) => {
    const input = evento.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (!input.name.startsWith('kotashi-plan')) return;

    // Solo se retocan las tarjetas del MISMO grupo (fuente o filtros) que el radio que
    // acaba de cambiar, para no desmarcar visualmente el otro grupo mientras está oculto.
    const grupo = input.closest('[data-plan-group]');
    const ambito = grupo ? grupo.querySelectorAll('.kotashi-plan') : this.querySelectorAll('.kotashi-plan');
    for (const etiqueta of ambito) {
      etiqueta.classList.toggle('is-activo', etiqueta.contains(input));
    }
  };

  /**
   * Maneja las 3 tarjetas (Negro, Blanco, Filtros) con UN solo listener de "click", no de
   * "change". Es a propósito: "change" solo salta cuando el radio pasa de no marcado a
   * marcado. Si Filtros (que no es un radio de color) se queda como tarjeta "activa" y luego
   * el cliente vuelve a pulsar Blanco -que por debajo seguía marcado desde antes-, el radio no
   * cambia de estado y el navegador NO dispara "change": la tarjeta se queda encallada en
   * Filtros y parece que el clic no hace nada. "click" salta siempre, pase lo que pase antes.
   */
  #alClicSwatch = (evento) => {
    const tarjeta = evento.target.closest('.kotashi-color');
    if (!tarjeta) return;

    const input = tarjeta.querySelector('input[type="radio"]');
    if (input?.disabled) return;

    for (const etiqueta of this.querySelectorAll('.kotashi-color')) {
      etiqueta.classList.toggle('is-activo', etiqueta === tarjeta);
    }

    if (input) {
      const nombre = this.querySelector('[data-color-actual]');
      if (nombre) nombre.textContent = input.value;
    }

    // Filtros tiene su propio "Modo de compra" con precios de filtros (no de la fuente):
    // se alterna cuál de los dos grupos se ve según la tarjeta activa.
    const esFiltros = tarjeta.hasAttribute('data-swatch-filtros');
    this.dataset.modo = esFiltros ? 'filtros' : 'fuente';

    const grupoFuente = this.querySelector('[data-plan-group="fuente"]');
    const grupoFiltros = this.querySelector('[data-plan-group="filtros"]');
    if (grupoFuente) grupoFuente.hidden = esFiltros;
    if (grupoFiltros) grupoFiltros.hidden = !esFiltros;

    this.#actualizarTitulo(esFiltros, input);
    this.#alternarGrupo('[data-lista-group="fuente"]', '[data-lista-group="filtros"]', esFiltros);
    this.#alternarGrupo('[data-panel-group="fuente"]', '[data-panel-group="filtros"]', esFiltros);

    this.#mostrarVista(tarjeta, input);
  };

  /**
   * El título ("Mizu Negra" / "Mizu Blanca" / "Filtros Mizu") vive fuera de este custom
   * element (es hermano de <kotashi-compra> dentro de .kotashi-ficha, igual que la galería),
   * así que se busca con this.closest('.kotashi-ficha') como en #mostrarVista. Las 3 variantes
   * ya vienen resueltas por Liquid en data-titulo-negro/blanco/filtros (con el span de color
   * incluido), para no duplicar ese HTML aquí.
   */
  #actualizarTitulo(esFiltros, input) {
    const titulo = this.closest('.kotashi-ficha')?.querySelector('[data-titulo]');
    if (!titulo) return;

    let html;
    if (esFiltros) html = titulo.dataset.tituloFiltros;
    else if (input?.value === 'Negro') html = titulo.dataset.tituloNegro;
    else html = titulo.dataset.tituloBlanco;

    if (html) titulo.innerHTML = html;
  }

  /**
   * Mismo patrón que "Modo de compra": dos versiones de un bloque (la de la fuente y la de
   * Filtros) viven ambas en el DOM y se alterna cuál se ve, en vez de reescribir contenido a
   * mano. Se reutiliza para los 4 puntos con check de arriba (data-lista-group) Y el panel de
   * iconos de más abajo (data-panel-group), que siguen la misma lógica fuente/filtros.
   */
  #alternarGrupo(selectorFuente, selectorFiltros, esFiltros) {
    const seccion = this.closest('.kotashi-ficha');
    if (!seccion) return;

    const elFuente = seccion.querySelector(selectorFuente);
    const elFiltros = seccion.querySelector(selectorFiltros);
    if (elFuente) elFuente.hidden = esFiltros;
    if (elFiltros) elFiltros.hidden = !esFiltros;
  }

  /**
   * Activa la miniatura real de la galería de la ficha (misma sección, columna izquierda)
   * para que el marco grande Y el resaltado de la miniatura cambien juntos. Si no hay galería
   * en esta página (p.ej. la ficha real de producto de Shopify), cae al cambio directo de la
   * foto grande.
   *
   * OJO: antes esto hacía `mini.click()` y dejaba que kotashi-galeria.js reaccionase a ese
   * click. El bug real (23 julio): kotashi-compra.js se carga como `type="module"`, que el
   * navegador trata como diferido igual que un `defer` normal, y su etiqueta sale ANTES en el
   * HTML que la de kotashi-galeria.js (porque el snippet de compra se renderiza antes en la
   * sección). Los dos scripts son diferidos, así que se ejecutan en ese mismo orden: cuando
   * `connectedCallback` llega al final (`#preseleccionarDesdeUrl`, para el ?modo=filtros o
   * ?variant=X que mandan las tarjetas de "Elige tu Mizu") y llama a este método, el listener
   * de clicks de kotashi-galeria.js TODAVÍA no existe -su script no ha llegado a ejecutarse-,
   * así que el `mini.click()` no lo pilla nadie y la vista se queda en la fuente negra por
   * defecto aunque el swatch ya marque Filtros o Blanco por dentro. Por eso ahora esta función
   * activa la miniatura y el marco directamente aquí (misma lógica que kotashi-galeria.js,
   * duplicada a propósito), sin depender de que el otro script ya se haya cargado.
   */
  #mostrarVista(tarjeta, input) {
    // data-imagen-grande vive en el <input> para los colores (el radio), y en la propia
    // tarjeta para Filtros (que es directamente un <button>, sin input dentro).
    const imagenGrande = input ? input.dataset.imagenGrande : tarjeta.dataset.imagenGrande;

    const seccion = this.closest('.kotashi-ficha');
    if (seccion) {
      const selector = input
        ? `.kotashi-ficha__mini[data-color="${CSS.escape(input.value)}"]`
        : '.kotashi-ficha__mini[data-indice="filtros"]';
      const mini = seccion.querySelector(selector);
      if (mini) {
        this.#activarMiniatura(mini);
        return;
      }
    }
    this.#actualizarImagen(imagenGrande);
  }

  /** Misma lógica que el listener de kotashi-galeria.js, para no depender de su timing. */
  #activarMiniatura(mini) {
    const galeria = mini.closest('.kotashi-ficha__galeria');
    if (!galeria) return;

    const indice = mini.dataset.indice;

    for (const boton of galeria.querySelectorAll('.kotashi-ficha__mini')) {
      const activo = boton === mini;
      boton.classList.toggle('is-activo', activo);
      boton.setAttribute('aria-selected', String(activo));
    }

    for (const marco of galeria.querySelectorAll('.kotashi-ficha__marco')) {
      marco.classList.toggle('is-oculto', marco.dataset.marco !== indice);
    }
  }

  #actualizarImagen(url) {
    if (!url) return;
    const seccion = this.closest('.kotashi-ficha');
    if (seccion) {
      for (const img of seccion.querySelectorAll('.kotashi-ficha__img')) {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = url;
      }
      return;
    }

    // Página de producto de Shopify: aquí la galería es la de Horizon (media-gallery), no
    // la de la ficha de la home. Se cambia la PRIMERA imagen del grid (la principal), que
    // es la respuesta visual que espera quien pulsa un swatch. El resto del grid se queda.
    const galeria = document.querySelector('media-gallery');
    const principal = galeria?.querySelector('img');
    if (principal) {
      principal.removeAttribute('srcset');
      principal.removeAttribute('sizes');
      principal.src = url;
    }
  }

  #alAñadir = async () => {
    const variantId = this.#variantId;
    if (!variantId) return;

    let lineas;

    if (this.#modo === 'filtros') {
      // Los filtros van SOLOS: una única línea, con selling_plan si ha elegido suscribirse
      // o sin él si es compra única. La fuente no entra en esta línea de compra.
      const linea = { id: Number(variantId), quantity: 1 };
      if (this.#esSuscripcion && this.dataset.haySuscripcion === 'true') {
        linea.selling_plan = Number(this.dataset.planId);
      }
      lineas = [linea];
    } else {
      lineas = [{ id: Number(variantId), quantity: 1 }];

      if (this.#esSuscripcion && this.dataset.haySuscripcion === 'true') {
        lineas.push({
          id: Number(this.dataset.filtrosId),
          quantity: 1,
          selling_plan: Number(this.dataset.planId),
        });
      }
    }

    this.#cargando(true);
    this.#mostrarError(null);

    // El cajón del carrito de Horizon escucha este evento para refrescarse.
    const diferido = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lineas.map((linea) => ({ merchandiseId: String(linea.id), quantity: linea.quantity })),
        promise: diferido.promise,
      })
    );

    try {
      const respuesta = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: lineas }),
      });
      const datos = await respuesta.json();

      if (!respuesta.ok || datos.status) {
        throw new Error(datos.description || datos.message || 'No se ha podido añadir al carrito.');
      }

      const carrito = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((r) => r.json());

      diferido.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(carrito),
        detail: {
          items: carrito.items,
          source: 'kotashi-compra',
          sourceId: this.id?.toString() ?? 'kotashi-compra',
          itemCount: lineas.length,
          didError: false,
        },
      });

      if (this.anuncio) this.anuncio.textContent = 'Añadido al carrito';
    } catch (error) {
      diferido.reject(error);
      this.#mostrarError(error.message);
    } finally {
      this.#cargando(false);
    }
  };

  #cargando(activo) {
    if (!this.boton) return;
    this.boton.setAttribute('aria-busy', String(activo));
    if (this.textoBoton) {
      this.textoBoton.textContent = activo ? 'Añadiendo…' : this.textoBotonOriginal;
    }
  }

  #mostrarError(mensaje) {
    if (!this.error) return;
    this.error.textContent = mensaje ?? '';
    this.error.hidden = !mensaje;
  }
}

if (!customElements.get('kotashi-compra')) {
  customElements.define('kotashi-compra', KotashiCompra);
}
